/**
 * Local personal-best store for the Heap Leach Slots easter egg.
 *
 * Records live in localStorage only — there is no backend for this. Entries are
 * keyed per player so two people signing in on the same machine keep separate
 * records instead of clobbering each other.
 *
 * Stored shape: { [playerKey]: { name, score, spins, updatedAt } }
 *
 * Every read/write is guarded: localStorage throws in private-browsing modes and
 * when the origin quota is full. A player whose storage is unavailable simply
 * plays without a saved record rather than crashing the modal.
 */

const STORAGE_KEY = 'slots.records.v1';

const isValidRecord = r =>
  !!r &&
  typeof r === 'object' &&
  typeof r.score === 'number' &&
  Number.isFinite(r.score);

const normalize = r => ({
  name: typeof r.name === 'string' && r.name ? r.name : 'You',
  score: r.score,
  spins: typeof r.spins === 'number' && Number.isFinite(r.spins) ? r.spins : 0,
  updatedAt: typeof r.updatedAt === 'string' ? r.updatedAt : null,
});

function readAll() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return {};
    const parsed = JSON.parse(raw);
    // Anything unexpected (hand-edited, older shape) is treated as "no records"
    if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) return {};
    return parsed;
  } catch {
    return {};
  }
}

function writeAll(records) {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(records));
    return true;
  } catch {
    return false;
  }
}

/**
 * Read one player's stored best.
 * @returns {{name: string, score: number, spins: number, updatedAt: string|null}|null}
 */
export function getRecord(playerKey) {
  if (!playerKey) return null;
  const stored = readAll()[playerKey];
  return isValidRecord(stored) ? normalize(stored) : null;
}

/**
 * Persist a score if — and only if — it beats the player's stored best.
 *
 * @returns {{record: object, persisted: boolean, isNewRecord: boolean}}
 *   record      the winning record (stored one when the new score didn't beat it)
 *   persisted   false when localStorage refused the write
 *   isNewRecord true when the incoming score replaced the stored best
 */
export function saveRecord(playerKey, { name, score, spins }) {
  const candidate = normalize({ name, score, spins, updatedAt: new Date().toISOString() });
  if (!playerKey || !Number.isFinite(score)) {
    return { record: candidate, persisted: false, isNewRecord: false };
  }

  const all = readAll();
  const existing = all[playerKey];
  if (isValidRecord(existing) && existing.score >= score) {
    return { record: normalize(existing), persisted: true, isNewRecord: false };
  }

  all[playerKey] = candidate;
  return { record: candidate, persisted: writeAll(all), isNewRecord: true };
}

/**
 * The alias this player last submitted to the global board, so they don't have
 * to retype it. Stored separately from records — it is a preference, not a score.
 */
const ALIAS_KEY = 'slots.alias.v1';

export function getSavedAlias(playerKey) {
  if (!playerKey) return '';
  try {
    const raw = localStorage.getItem(ALIAS_KEY);
    if (!raw) return '';
    const parsed = JSON.parse(raw);
    const alias = parsed && typeof parsed === 'object' ? parsed[playerKey] : null;
    return typeof alias === 'string' ? alias : '';
  } catch {
    return '';
  }
}

export function saveAlias(playerKey, alias) {
  if (!playerKey || typeof alias !== 'string') return false;
  try {
    const raw = localStorage.getItem(ALIAS_KEY);
    let parsed = {};
    try {
      const candidate = JSON.parse(raw || '{}');
      if (candidate && typeof candidate === 'object' && !Array.isArray(candidate)) {
        parsed = candidate;
      }
    } catch {
      parsed = {};
    }
    parsed[playerKey] = alias;
    localStorage.setItem(ALIAS_KEY, JSON.stringify(parsed));
    return true;
  } catch {
    return false;
  }
}

/** Forget one player's record. Other players on this machine are untouched. */
export function clearRecord(playerKey) {
  if (!playerKey) return false;
  const all = readAll();
  if (!(playerKey in all)) return true;
  delete all[playerKey];
  return writeAll(all);
}
